import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Volume2, VolumeX } from "lucide-react";

const BG_TARGET_VOLUME = 0.18;
const UNLOCK_EVENTS = ["pointerdown", "keydown", "touchstart", "wheel"];

export const AudioGreeting = () => {
  const bgRef = useRef(null);
  const bgFadeRef = useRef(null);
  const [introDone, setIntroDone] = useState(false);
  const [bgPlaying, setBgPlaying] = useState(false);
  const [bgMuted, setBgMuted] = useState(false);
  const introDoneRef = useRef(false);
  const bgMutedRef = useRef(false);

  const fadeBg = (target, duration = 3000, onDone) => {
    cancelAnimationFrame(bgFadeRef.current);
    const bg = bgRef.current;
    if (!bg) return;
    const start = bg.volume;
    const t0 = performance.now();
    const step = (t) => {
      const p = Math.min((t - t0) / duration, 1);
      bg.volume = Math.max(0, Math.min(1, start + (target - start) * p));
      if (p < 1) bgFadeRef.current = requestAnimationFrame(step);
      else onDone?.();
    };
    bgFadeRef.current = requestAnimationFrame(step);
  };

  const stopBg = (duration = 600) => {
    const bg = bgRef.current;
    setBgPlaying(false);
    if (!bg || bg.paused) return;
    fadeBg(0, duration, () => bg.pause());
  };

  const startBg = () => {
    const bg = bgRef.current;
    if (!bg || !introDoneRef.current || bgMutedRef.current || !bg.paused) return;
    bg.volume = 0;
    bg
      .play()
      .then(() => {
        if (bgMutedRef.current || !introDoneRef.current) {
          bg.pause();
          setBgPlaying(false);
          return;
        }
        setBgPlaying(true);
        fadeBg(BG_TARGET_VOLUME, 3000);
      })
      .catch(() => {
        const unlock = () => {
          UNLOCK_EVENTS.forEach((e) => window.removeEventListener(e, unlock));
          startBg();
        };
        UNLOCK_EVENTS.forEach((e) =>
          window.addEventListener(e, unlock, { once: true, passive: true })
        );
      });
  };

  useEffect(() => {
    const markIntroDone = () => {
      introDoneRef.current = true;
      setIntroDone(true);
      startBg();
    };
    if (window.__cobIntroEnded) markIntroDone();
    else window.addEventListener("cob:intro-ended", markIntroDone, { once: true });

    return () => {
      cancelAnimationFrame(bgFadeRef.current);
      window.removeEventListener("cob:intro-ended", markIntroDone);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleBg = () => {
    const next = !bgMutedRef.current;
    bgMutedRef.current = next;
    setBgMuted(next);
    if (next) stopBg(300);
    else startBg();
  };

  return (
    <>
      <audio ref={bgRef} src="/api/files/cob-teresina/audio/musica-fundo.mp3" type="audio/mpeg" preload="none" loop />

      <AnimatePresence>
        {introDone && (
          <motion.button
            type="button"
            data-testid="bg-music-toggle"
            aria-label={bgMuted ? "Ativar música de fundo" : "Desativar música de fundo"}
            title={bgMuted ? "Ativar música de fundo" : "Desativar música de fundo"}
            onClick={toggleBg}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ delay: 1.5, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="btn-press fixed bottom-5 left-5 z-[60] flex h-10 w-10 items-center justify-center rounded-full border border-cob-gold/50 bg-cob-dark/95 text-cob-gold shadow-[0_14px_35px_-12px_rgba(6,63,50,0.55)] backdrop-blur-sm transition-colors hover:bg-cob-green hover:text-white"
          >
            {bgMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className={`h-4 w-4 ${bgPlaying ? "" : "opacity-60"}`} />}
          </motion.button>
        )}
      </AnimatePresence>
    </>
  );
};
